import { useEffect, useState } from 'react'
import { performUndo, setUndoNotifier, undoEntry } from '@aurora/platform'
import { dismissToast, pushToast, subscribeToasts, type Toast } from '../lib/toast'

/**
 * The app's confirmations, and the way back from them.
 *
 * The platform's undo stack has no opinion about presentation — it calls the
 * notifier installed here. Installing it once, at the root, is what makes every
 * plan edit reversible without a single component knowing that undo exists.
 *
 * It sits above the tab bar rather than at the top of the screen: the hand that
 * made the change is already down there, and an undo you have to reach for is
 * one you will not take.
 */

const LIFETIME_MS = 6000

export function ToastLayer() {
  const [toasts, setToasts] = useState<Toast[]>([])

  useEffect(() => {
    setUndoNotifier({
      notify: (message) => pushToast(message),
      notifyWithUndo: (label, id) => pushToast(label, { undo: () => undoEntry(id) }),
    })
    return subscribeToasts(setToasts)
  }, [])

  // Cmd/Ctrl+Z anywhere outside a text field. On desktop this is the gesture a
  // parent already has in their hands.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'z' || !(e.metaKey || e.ctrlKey) || e.shiftKey) return
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      if (performUndo()) e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <ToastRow key={t.id} toast={t} />
      ))}
    </div>
  )
}

function ToastRow({ toast }: { toast: Toast }) {
  useEffect(() => {
    // Errors are the one thing worth interrupting for, so they stay put.
    if (toast.tone === 'error') return
    const timer = setTimeout(() => dismissToast(toast.id), LIFETIME_MS)
    return () => clearTimeout(timer)
  }, [toast.id, toast.tone])

  return (
    <div className={`toast${toast.tone === 'error' ? ' is-error' : ''}`}>
      <span className="toast-message">{toast.message}</span>

      {toast.undo && (
        <button
          className="toast-action"
          onClick={() => {
            toast.undo?.()
            dismissToast(toast.id)
          }}
        >
          Undo
        </button>
      )}

      <button className="toast-dismiss" onClick={() => dismissToast(toast.id)} aria-label="Dismiss">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  )
}
